import React from 'react';
import { Box, useTheme } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import DisplayContent from './DisplayContent';

const ResponsiveDisplayGrid = ({ displayContent }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // < 600px
  const isTablet = useMediaQuery(theme.breakpoints.down('md')); // < 900px
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg')); // >= 1200px
  //console.log("ResponsiveDisplayGrid displayContent:", displayContent, "isMobile:", isMobile, "isTablet:", isTablet, "isDesktop:", isDesktop);

  if (!displayContent || !displayContent.items?.length) return null;

  const { items, type, marginBottom = '24px' } = displayContent;
  const itemCount = items.length;

  // Calculate how many items can fit per row based on screen size
  const getItemsPerRow = () => {
    if (isMobile) return 1; // Always stack on mobile
    if (isTablet) return 2;  // Max 2 on tablet
    return 3; // Max 3 on desktop
  };

  // Smart grid logic based on item count and screen size
  const getGridConfiguration = () => {
    const maxPerRow = getItemsPerRow();
    
    if (itemCount === 1) {
      return { columns: 1, rows: [[0]] };
    }
    
    if (itemCount === 2) {
      return { columns: Math.min(2, maxPerRow), rows: maxPerRow >= 2 ? [[0, 1]] : [[0], [1]] };
    }
    
    if (itemCount === 3) {
      if (maxPerRow >= 3) {
        return { columns: 3, rows: [[0, 1, 2]] };
      } else if (maxPerRow === 2) {
        return { columns: 2, rows: [[0, 1], [2]] }; // 2+1 centered
      } else {
        return { columns: 1, rows: [[0], [1], [2]] };
      }
    }
    
    if (itemCount === 4) {
      if (maxPerRow >= 2) {
        // Always do 2x2 for 4 items, even if 3 could fit
        return { columns: 2, rows: [[0, 1], [2, 3]] };
      } else {
        return { columns: 1, rows: [[0], [1], [2], [3]] };
      }
    }
    
    // For 5+ items, fill rows optimally
    if (itemCount >= 5) {
      const rows = [];
      let currentIndex = 0;
      
      while (currentIndex < itemCount) {
        const row = [];
        const remainingItems = itemCount - currentIndex;
        const itemsInThisRow = Math.min(maxPerRow, remainingItems);
        
        for (let i = 0; i < itemsInThisRow; i++) {
          row.push(currentIndex++);
        }
        rows.push(row);
      }
      
      return { columns: maxPerRow, rows };
    }
  };

  const { columns, rows } = getGridConfiguration();

  return (
    <Box sx={{ 
      marginBottom,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '16px',
      width: '100%'
    }}>
      {rows.map((row, rowIndex) => (
        <Box
          key={rowIndex}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '16px',
            width: '100%',
            flexWrap: 'nowrap'
          }}
        >
          {row.map((itemIndex) => (
            <DisplayContent
              key={itemIndex}
              type={type}
              info={items[itemIndex]}
            />
          ))}
        </Box>
      ))}
    </Box>
  );
};

// Updated renderDisplayContent function for TemplatedGameHomePage
const renderDisplayContent = (displayContent) => {
  if (!displayContent || !displayContent.items?.length) return null;

  return <ResponsiveDisplayGrid displayContent={displayContent} />;
};

export default ResponsiveDisplayGrid;